import React, { useState, useEffect, useMemo } from 'react';

function CryptoSelector({ selectedPair, onPairChange }) {
  const [isOpen, setIsOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [allPairs, setAllPairs] = useState([]);
  const [filteredPairs, setFilteredPairs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [iconError, setIconError] = useState(false);

  useEffect(() => {
    fetchTradingPairs();
  }, []);

  useEffect(() => {
    if (searchTerm) {
      const filtered = allPairs.filter(pair =>
        pair.baseAsset.toLowerCase().includes(searchTerm.toLowerCase()) ||
        pair.displayName.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredPairs(filtered);
    } else {
      setFilteredPairs(allPairs);
    }
  }, [searchTerm, allPairs]);

  const fetchTradingPairs = async () => {
    try {
      const response = await fetch('/api/crypto/pairs');
      const result = await response.json();

      if (result.success) {
        setAllPairs(result.data);
        setFilteredPairs(result.data);
      }
    } catch (error) {
      console.error('Error fetching trading pairs:', error);
    } finally {
      setLoading(false);
    }
  };

  // Reset icon error when selected pair changes
  useEffect(() => {
    setIconError(false);
  }, [selectedPair?.baseAsset]);

  const handleSelectPair = (pair) => {
    onPairChange(pair);
    setIsOpen(false);
    setSearchTerm('');
  };

  const getCryptoIcon = (symbol) => {
    // Use cryptocurrency-icons CDN which is more reliable
    return `https://cdn.jsdelivr.net/npm/cryptocurrency-icons@0.18.1/svg/color/${symbol.toLowerCase()}.svg`;
  };

  // Memoize the selected icon URL to prevent unnecessary re-renders
  const selectedIconUrl = useMemo(() => {
    return getCryptoIcon(selectedPair?.baseAsset || 'BTC');
  }, [selectedPair?.baseAsset]);

  return (
    <div className="crypto-selector">
      <button
        className="selector-button"
        onClick={() => setIsOpen(!isOpen)}
      >
        <div className="selected-pair">
          <img
            src={iconError ? '/default-crypto-icon.svg' : selectedIconUrl}
            alt={selectedPair?.baseAsset}
            className="crypto-icon"
            onError={(e) => {
              if (!iconError) {
                setIconError(true);
                e.target.src = '/default-crypto-icon.svg';
              }
            }}
          />
          <span className="pair-name">
            {selectedPair?.displayName || 'Select Pair'}
          </span>
        </div>
        <span className="dropdown-arrow">{isOpen ? '▲' : '▼'}</span>
      </button>

      {isOpen && (
        <div className="selector-dropdown">
          <div className="search-box">
            <input
              type="text"
              placeholder="Search cryptocurrency..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              autoFocus
            />
          </div>

          <div className="pairs-list">
            {loading ? (
              <div className="loading">Loading pairs...</div>
            ) : filteredPairs.length > 0 ? (
              filteredPairs.map((pair) => (
                <div
                  key={pair.symbol}
                  className={`pair-item ${selectedPair?.symbol === pair.symbol ? 'active' : ''}`}
                  onClick={() => handleSelectPair(pair)}
                >
                  <img
                    src={getCryptoIcon(pair.baseAsset)}
                    alt={pair.baseAsset}
                    className="crypto-icon"
                    onError={(e) => { e.target.src = '/default-crypto-icon.svg'; }}
                  />
                  <div className="pair-info">
                    <span className="base-asset">{pair.baseAsset}</span>
                    <span className="quote-asset">/{pair.quoteAsset}</span>
                  </div>
                </div>
              ))
            ) : (
              <div className="no-results">No pairs found</div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}

export default CryptoSelector;
